import {
  runScenarios,
  DCFAssumptions,
  DCFValuation,
  validateScenarioConstraints,
  DEFAULT_SCENARIO_CONSTRAINTS,
} from "../lib/dcfEngine";
export function computeType4TerminalValue(
  tbvPerShare: number,
  rotce: number,
  retentionRate: number,
  sharesOutstandingInMillions: number,
  selectedPtbvMultiple: number,
) {
  // BUG-1b FIX: cap the implied book-growth rate so a high ROTCE × high retention
  // cannot compound tangible book to an implausible 10-yr multiple (which drives the
  // P/TBV terminal to dominate and terminalToInterimPvRatio to explode). 0.18 ⇒
  // ~5.2x over 10yr, already aggressive for a mature financial.
  const _rawBookGrowth = rotce * Math.max(0, retentionRate);
  const _cappedBookGrowth = Math.min(_rawBookGrowth, 0.18);
  const year10TbvPerShare =
    tbvPerShare * Math.pow(1 + _cappedBookGrowth, 10);
  const projectedYear10TBVTotal =
    year10TbvPerShare * sharesOutstandingInMillions;
  const terminalValue = selectedPtbvMultiple * projectedYear10TBVTotal;

  return {
    year10TbvPerShare,
    projectedYear10TBVTotal,
    terminalValue,
  };
}

// ─── §42 Residual‑Income Type‑4 valuation (RI‑1, Tuning v2.79 §47) ──────────
// Deterministic. No LLM. Kills the P/TBV terminal + interim‑OE double‑count by
// construction: value = current tangible book + PV of returns earned ABOVE the
// cost of equity, faded via an Ohlson persistence factor. Excess returns above
// Ke create value; a business earning below Ke (e.g. a degenerate 1‑yr ROTCE)
// is correctly valued BELOW book — self‑diagnosing.
//   RI_t   = (rotce − ke) * B_{t-1}                 (per share, on beginning book)
//   B_t    = B_{t-1} * (1 + rotce * max(0,retention))
//   CV_N   = (omega / (1 + ke − omega)) * RI_N       (Ohlson continuing residual)
//   V/sh   = B_0 + Σ_{t=1..N} RI_t/(1+ke)^t + CV_N/(1+ke)^N
// omega ∈ [0,0.99] (0 = excess vanishes after the explicit window; higher =
// more durable moat). Read once from env TYPE4_RI_PERSISTENCE (default 0.60),
// clamped; a single named tunable so the RI‑1 corpus can calibrate it.
export function computeType4ResidualIncomeValue(
  tbvPerShare: number,
  rotce: number,
  retentionRate: number,
  costOfEquity: number,
  sharesOutstandingInMillions: number,
  explicitYears: number = 10,
) {
  if (!(tbvPerShare > 0) || !isFinite(rotce) || !isFinite(costOfEquity)) {
    return null;
  }
  const ke = costOfEquity;
  const ret = Math.max(0, retentionRate);
  const rawOmega = parseFloat(process.env.TYPE4_RI_PERSISTENCE || "0.60");
  const omega = Math.min(0.99, Math.max(0, isFinite(rawOmega) ? rawOmega : 0.60));

  let book = tbvPerShare;
  let pvResidual = 0;
  let lastRi = 0;
  // RI‑3 display: capture the per‑year walk as it is computed, so the memo can
  // render a deterministic, additive projection panel from the SAME loop that
  // produces the aggregate IV (no re‑derivation, no drift). All per‑share.
  const walk: Array<{
    year: number;
    beginningBook: number;
    rotce: number;
    residualIncome: number;
    pvResidualIncome: number;
  }> = [];
  for (let t = 1; t <= explicitYears; t++) {
    const beginningBook = book;
    const ri = (rotce - ke) * book; // excess return on beginning book
    const pvRi = ri / Math.pow(1 + ke, t);
    pvResidual += pvRi;
    lastRi = ri;
    walk.push({ year: t, beginningBook, rotce, residualIncome: ri, pvResidualIncome: pvRi });
    book = book * (1 + rotce * ret); // book grows by retained earnings
  }
  // Ohlson continuing value on the final explicit RI, discounted N years.
  const continuingRi = (omega / (1 + ke - omega)) * lastRi;
  const pvContinuing = continuingRi / Math.pow(1 + ke, explicitYears);

  const riValuePerShare = tbvPerShare + pvResidual + pvContinuing;
  const riEquityValueTotal = riValuePerShare * sharesOutstandingInMillions;
  const impliedPtbvFromRi = tbvPerShare > 0 ? riValuePerShare / tbvPerShare : null;

  return {
    riValuePerShare,
    riEquityValueTotal,
    impliedPtbvFromRi,
    omegaUsed: omega,
    year10BookPerShare: book,
    // RI‑3 display fields. Additive identity (holds to floating precision):
    //   tangibleBookAnchorPerShare + sumPvResidualPerShare + pvContinuingPerShare === riValuePerShare
    walk,
    tangibleBookAnchorPerShare: tbvPerShare,
    sumPvResidualPerShare: pvResidual,
    continuingResidualPerShare: continuingRi,
    pvContinuingPerShare: pvContinuing,
    keUsed: ke,
  };
}
// ─── END §42 Residual‑Income Type‑4 valuation ──────────────────────────────

export function getDeterministicData(
  ticker: string,
  data: any,
  entityType: string = "Type 6",
  exchangeRate: number = 1,
  priceCurrency: string = "USD",
  reportedCurrency: string = "USD",
  type5Origin: string | null = null,
) {
  if (!data) return null;

  // Helper to convert local currency values to USD
  const toUsd = (val: number | undefined | null) => {
    if (val === undefined || val === null || isNaN(val)) return 0;
    return val / exchangeRate;
  };

  const profile = data.profile || {};
  const polygon = data.polygon || {};
  const quote = data.quote || {};
  const metrics = data.metrics || {};
  const ratios = data.ratios || {};
  const income = data.income?.[0] || {};
  const balance = data.balance?.[0] || {};
  const cashFlow = data.cashFlow?.[0] || {};

  const avOverview = data.alphaVantage?.overview || {};
  const avQuote = data.alphaVantage?.quote?.["Global Quote"] || {};
  const avIncome =
    data.alphaVantage?.income?.annualReports?.[0] ||
    data.alphaVantage?.income?.quarterlyReports?.[0] ||
    {};
  const avBalance =
    data.alphaVantage?.balance?.annualReports?.[0] ||
    data.alphaVantage?.balance?.quarterlyReports?.[0] ||
    {};
  const avCashFlow =
    data.alphaVantage?.cashFlow?.annualReports?.[0] ||
    data.alphaVantage?.cashFlow?.quarterlyReports?.[0] ||
    {};

  // Robust Net Debt calculation
  const totalDebt = toUsd(
    balance.totalDebt ||
      (balance.shortTermDebt || 0) + (balance.longTermDebt || 0) ||
      parseFloat(avBalance.shortLongTermDebtTotal) ||
      0 ||
      parseFloat(avBalance.totalDebt) ||
      0 ||
      0,
  );
  const cashAndEquiv = toUsd(
    balance.cashAndCashEquivalents ||
      parseFloat(avBalance.cashAndCashEquivalentsAtCarryingValue) ||
      0 ||
      parseFloat(avBalance.cashAndShortTermInvestments) ||
      0 ||
      0,
  );
  const shortTermInvest = toUsd(
    balance.shortTermInvestments ||
      parseFloat(avBalance.shortTermInvestments) ||
      0 ||
      0,
  );
  const longTermInvest = toUsd(
    balance.longTermInvestments ||
      parseFloat(avBalance.longTermInvestments) ||
      0 ||
      0,
  );

  // FMP uses 'mktCap' in profile, 'marketCap' in quote. Polygon uses 'marketCap'.
  // Market cap for ADRs is usually already in USD in the quote/profile.
  const mktCap =
    profile.mktCap ||
    profile.marketCap ||
    quote.marketCap ||
    polygon.marketCap ||
    parseFloat(avOverview.MarketCapitalization) ||
    0 ||
    0;

  // For elite large caps, longTermInvestments are almost always marketable securities (cash-like).
  // EXCEPTION: Financial/insurance entities (SIC 6020-6399). Their longTermInvestments are the
  // business's asset base (equity portfolios, bond holdings, insurance float deployment) —
  // not cash equivalents. Including them produces a wildly negative "net debt" that inflates IV.
  const isEliteLargeCap = mktCap > 500000000000; // > $500B
  const financialSicRaw = profile.sicCode || "";
  const financialSic = parseInt(String(financialSicRaw)) || 0;
  const isFinancialEntity =
    (financialSic >= 6020 && financialSic <= 6099) ||
    (financialSic >= 6150 && financialSic <= 6199) ||
    (financialSic >= 6311 && financialSic <= 6399);
  // Financial entities: exclude longTermInvestments from net debt — they are the business.
  // Non-financial elite large caps: include longTermInvestments as cash-like (existing behavior).
  const calculatedNetDebt =
    totalDebt -
    cashAndEquiv -
    shortTermInvest -
    (isEliteLargeCap && !isFinancialEntity ? longTermInvest : 0);

  // Robust FCF calculation
  const annualOcf = toUsd(
    cashFlow.netCashProvidedByOperatingActivities ||
      parseFloat(avCashFlow.operatingCashflow) ||
      0 ||
      0,
  );
  const annualCapex = toUsd(
    Math.abs(
      cashFlow.capitalExpenditure ||
        parseFloat(avCashFlow.capitalExpenditures) ||
        0 ||
        0,
    ),
  );
  const annualFcf = annualOcf - annualCapex;

  const cashFlowQuarterly = data.cashFlowQuarterly || [];
  if (ticker === "SNDK") {
    console.log(
      `[DEBUG-SNDK] cashFlowQuarterly length: ${cashFlowQuarterly.length}`,
    );
  }
  const ttmFcf =
    cashFlowQuarterly.length > 0
      ? toUsd(
          cashFlowQuarterly.reduce(
            (acc: number, curr: any) => acc + (curr.freeCashFlow || 0),
            0,
          ),
        ) *
        (4 / cashFlowQuarterly.length)
      : 0;

  const calculatedFcf = Math.max(annualFcf, ttmFcf);

  // Synthetic Owner Earnings calculation (Buffett's formula)
  const netIncome = toUsd(
    income.netIncome || parseFloat(avIncome.netIncome) || 0 || 0,
  );
  const depAmort = toUsd(
    cashFlow.depreciationAndAmortization ||
      parseFloat(avCashFlow.depreciationDepletionAndAmortization) ||
      0 ||
      0,
  );

  // MAINTENANCE CAPEX CONFIDENCE BANDS (base case used for IV; high case for bear scenario)
  // Low  (60%): underestimates maintenance — used only to surface optimistic ceiling
  // Base (archetype-driven): used for normalized owner earnings → IV
  // High (85-100%): stress case for capital-heavy industries
  const isCapitalHeavy = [
    "Utilities",
    "Energy",
    "Materials",
    "Industrials",
    "Telecom",
  ].some((s) => (profile.sector || avOverview.Sector || "").includes(s));
  const maintenanceCapexRatioLow = 0.6;
  const maintenanceCapexRatioBase = isEliteLargeCap
    ? 0.8
    : isCapitalHeavy
      ? 0.75
      : 0.6;
  const maintenanceCapexRatioHigh = isCapitalHeavy ? 1.0 : 0.85;

  const maintenanceCapexLow = annualCapex * maintenanceCapexRatioLow;
  const maintenanceCapexBase = annualCapex * maintenanceCapexRatioBase;
  const maintenanceCapexHigh = annualCapex * maintenanceCapexRatioHigh;

  // For Type 5 entities: use TTM FCF if it's positive (genuine standalone data).
  // If TTM FCF is also negative, both data sources are contaminated by parent-company
  // consolidated data. Flag this so the engine can handle it gracefully rather than
  // building a DCF on fabricated negative numbers.
  const isType5Entity = entityType === "Type 5";
  const ttmFcfForOE = ttmFcf; // alias: ttmFcf is the TTM FCF computed from cashFlowQuarterly above
  const usesTtmAnchor = isType5Entity && ttmFcfForOE > 0;
  const isType5DataContaminated =
    isType5Entity &&
    ttmFcfForOE <= 0 &&
    netIncome + depAmort - maintenanceCapexBase <= 0;

  const type5UnvaluableReason: 'parent-contamination' | 'no-earnings-anchor' | null =
    isType5DataContaminated
      ? (type5Origin === 'ipo' ? 'no-earnings-anchor' : 'parent-contamination')
      : null;

  if (type5UnvaluableReason === 'parent-contamination') {
    console.warn(
      `[TYPE5-CONTAMINATED] ${ticker}: Both annual FCF ($${((netIncome + depAmort - maintenanceCapexBase) / 1000000).toFixed(0)}M) ` +
        `and TTM FCF ($${(ttmFcfForOE / 1000000).toFixed(0)}M) are negative for a Type 5 entity. ` +
        `Data likely reflects parent company consolidated operations, not standalone performance. ` +
        `Switching to qualitative-only mode — no DCF will be anchored to these numbers.`,
    );
  } else if (type5UnvaluableReason === 'no-earnings-anchor') {
    console.warn(
      `[TYPE5-UNANCHORED-IPO] ${ticker}: Recent IPO with negative annual AND TTM FCF — genuine standalone losses, no positive owner-earnings anchor. Switching to qualitative-only mode — no DCF will be anchored to these numbers.`
    );
  }

  const syntheticOwnerEarnings = isType5DataContaminated
    ? 0 // Contaminated — do not anchor to negative parent-company data
    : usesTtmAnchor
      ? ttmFcfForOE
      : netIncome + depAmort - maintenanceCapexBase;

  const syntheticOwnerEarningsLow = isType5DataContaminated
    ? 0
    : usesTtmAnchor
      ? ttmFcfForOE * 1.1
      : netIncome + depAmort - maintenanceCapexLow;

  const syntheticOwnerEarningsHigh = isType5DataContaminated
    ? 0
    : usesTtmAnchor
      ? ttmFcfForOE * 0.7
      : netIncome + depAmort - maintenanceCapexHigh;

  if (usesTtmAnchor) {
    console.log(
      `[TYPE5-OE-ANCHOR] ${ticker}: Using TTM FCF ($${(ttmFcfForOE / 1000000).toFixed(0)}M) as owner earnings anchor instead of stale annual data. Annual netIncome was $${(netIncome / 1000000).toFixed(0)}M (likely reflects parent company consolidated data).`,
    );
  }

  // Prefer FMP for financial data if available (user is on paid plan)
  const name =
    profile.companyName ||
    profile.name ||
    quote.name ||
    polygon.name ||
    avOverview.Name ||
    ticker;
  const price =
    profile.price ||
    quote.price ||
    polygon.price ||
    parseFloat(avQuote["05. price"]) ||
    0 ||
    0;

  // FMP uses 'changes' in profile, 'change' in quote. Polygon uses 'change'.
  const changes =
    profile.changes !== undefined
      ? profile.changes
      : quote.change !== undefined
        ? quote.change
        : polygon.change !== undefined
          ? polygon.change
          : parseFloat(avQuote["09. change"]) || 0;

  // SHARES-SOURCE PRIORITY ORDER:
  // 1. weightedAverageShsOut from the income statement — audited, filed, stable.
  //    This is the only source that does not fluctuate with market price or float estimates.
  //    FMP stable/income-statement[0].weightedAverageShsOut is sourced from 10-K/10-Q filings.
  // 2. commonStockSharesOutstanding from the balance sheet — also filed, slightly less preferred
  //    because it reflects the exact period-end count rather than the weighted average.
  // 3. profile.sharesOutstanding — live FMP float estimate, fluctuates daily. Use only as fallback.
  // 4. quote.sharesOutstanding — live quote field, same issue as profile. Secondary fallback.
  // 5. mktCap / price — derived and most volatile. Last resort only.
  // 6. Alpha Vantage OVERVIEW — filing-sourced but on AV's update schedule. Last resort.

  const incomeStatementShares =
    data.income?.[0]?.weightedAverageShsOut ||
    data.income?.[0]?.weightedAverageShsOutDil ||
    0;
  const balanceSheetShares =
    data.balance?.[0]?.commonStockSharesOutstanding || 0;

  const sharesFloatResult = data.sharesFloatResult;
  const floatOutstandingShares: number | undefined =
    Array.isArray(sharesFloatResult) && sharesFloatResult.length > 0
      ? (sharesFloatResult[0].outstandingShares ?? undefined)
      : undefined;

  // SHARES-SOURCE FIX (Jun 2026): prioritize filed/audited income-statement shares
  // over the live shares-float estimate, matching the documented priority order above.
  // FMP shares-float.outstandingShares can be stale/corrupted (e.g. CRM returned a
  // round 819,000,000 vs 950M filed). The income statement is the ONLY independent
  // source — FMP's marketCap is float_shares × price, so market-cap checks are circular.
  // (incomeStatementShares and balanceSheetShares are already `|| 0`, so `||` falls
  // through correctly when a source is absent.)
  const resolvedShares =
    incomeStatementShares ||
    balanceSheetShares ||
    floatOutstandingShares ||
    Math.round(mktCap / (price || 1));

  let shares = resolvedShares || parseFloat(avOverview.SharesOutstanding) || 0;

  console.log(
    `[SHARES-SOURCE] ${ticker}: floatOutstandingShares=${floatOutstandingShares ?? 'undefined'}, ` +
    `incomeStatementShares=${incomeStatementShares || 'undefined'}, ` +
    `balanceSheetShares=${balanceSheetShares || 'undefined'}, ` +
    `mktCap/price=${Math.round(mktCap / (price || 1))}, ` +
    `final shares selected=${resolvedShares}, ` +
    `final shares in millions=${Math.round(resolvedShares / 1_000_000)}M`
  );

  // [SHARES-RECONCILE] The income statement (filed) and shares-float (live estimate)
  // are the two independent share sources. A material divergence means FMP's float
  // field is likely stale/corrupted for this ticker. Measurement only — serves the
  // Tuning Log FMP-shares-reliability watch item.
  if (floatOutstandingShares && incomeStatementShares) {
    const shrDiv =
      Math.abs(floatOutstandingShares - incomeStatementShares) / incomeStatementShares;
    if (shrDiv > 0.10) {
      console.warn(
        `[SHARES-RECONCILE] ${ticker}: shares-float=${Math.round(floatOutstandingShares / 1e6)}M ` +
        `vs income-statement=${Math.round(incomeStatementShares / 1e6)}M ` +
        `(${(shrDiv * 100).toFixed(1)}% divergence) — using income-statement (filed). ` +
        `FMP float field likely stale/corrupted.`,
      );
    }
  }

  // Berkshire Hathaway Special Handling
  if (ticker.startsWith("BRK") && mktCap > 100000000000) {
    const impliedMktCap = shares * price;
    if (Math.abs(impliedMktCap - mktCap) / mktCap > 0.5) {
      shares = mktCap / (price || 1);
    }
  }

  const lastDiv =
    profile.lastDiv ||
    quote.lastDiv ||
    quote.dividend ||
    parseFloat(avOverview.ForwardAnnualDividendRate) ||
    0 ||
    0;
  // ─── BUG-1a FIX (retention): payout-adjusted, never defaults to 1.00 ─────────
  // The prior logic pinned retention to 1.0 whenever the dividend or OE base was
  // missing, which for dividend/buyback-heavy financials forced implied book
  // growth g = rotce * 1.0 up toward/above ke and blew up the Type-4 terminal
  // (terminalToInterimPvRatio in the thousands). Retention is now derived from
  // TOTAL shareholder payout (dividends + net buybacks) over net income to common,
  // with a hard [0, 0.90] clamp and a conservative 0.60 fallback — never 1.00.
  const _niCommonRet = netIncome; // toUsd-normalized, defined above
  const _divPaidRet =
    Math.abs(toUsd(cashFlow.dividendsPaid || 0)) ||
    (lastDiv > 0 && shares > 0 ? lastDiv * shares : 0);
  const _buybacksRet = Math.abs(toUsd(cashFlow.commonStockRepurchased || 0));
  const _payoutRet = _divPaidRet + _buybacksRet;
  const retentionRate = (() => {
    if (isFinite(_niCommonRet) && _niCommonRet > 0 && isFinite(_payoutRet)) {
      return Math.min(0.9, Math.max(0, 1 - _payoutRet / _niCommonRet));
    }
    // Secondary: dividend-only path when we have a per-share OE base.
    if (lastDiv > 0 && syntheticOwnerEarnings > 0 && shares > 0) {
      return Math.min(
        0.9,
        Math.max(0, 1 - lastDiv / (syntheticOwnerEarnings / shares)),
      );
    }
    return 0.6; // conservative default — NEVER 1.00
  })();

  // Calculate Dividend Yield %
  let divYield =
    ratios.dividendYieldTTM ||
    metrics.dividendYieldTTM ||
    ratios.dividendYield ||
    metrics.dividendYield ||
    parseFloat(avOverview.DividendYield) ||
    0;

  let dividendYieldPercent = 0;
  if (divYield > 0) {
    dividendYieldPercent = divYield * 100;
  } else if (ratios.dividendYieldPercentage !== undefined) {
    dividendYieldPercent = ratios.dividendYieldPercentage;
  } else if (lastDiv > 0 && price > 0) {
    // If lastDiv is in local currency but price is in USD, we need to convert lastDiv
    // But many ADRs have USD dividends. We assume if mismatch, lastDiv might need conversion.
    const normalizedLastDiv =
      priceCurrency === "USD" && reportedCurrency !== "USD"
        ? toUsd(lastDiv)
        : lastDiv;
    dividendYieldPercent = (normalizedLastDiv / price) * 100;
  }

  let high52 =
    quote.yearHigh ||
    profile.yearHigh ||
    (profile.range ? parseFloat(profile.range.split("-")[1]) : null) ||
    polygon.yearHigh ||
    (parseFloat(avQuote["07. latest day"])
      ? null
      : parseFloat(avQuote["03. high"])) ||
    parseFloat(avOverview["52WeekHigh"]) ||
    null;
  let low52 =
    quote.yearLow ||
    profile.yearLow ||
    (profile.range ? parseFloat(profile.range.split("-")[0]) : null) ||
    polygon.yearLow ||
    (parseFloat(avQuote["07. latest day"])
      ? null
      : parseFloat(avQuote["04. low"])) ||
    parseFloat(avOverview["52WeekLow"]) ||
    null;

  const bookEquityValue = toUsd(
    balance.totalStockholdersEquity ||
      parseFloat(avBalance.totalShareholderEquity) ||
      0 ||
      0,
  );

  const baseData = {
    name: name,
    ticker: ticker,
    description: profile.description || avOverview.Description || "",
    sector: profile.sector || avOverview.Sector || "",
    industry: profile.industry || avOverview.Industry || "",
    fullTimeEmployees:
      parseInt((profile.fullTimeEmployees as string) ||
               (avOverview.FullTimeEmployees as string) || "0", 10) || 0,
    currentPrice: price,
    priceChange: changes,
    priceChangePercent:
      price && changes ? (changes / (price - changes)) * 100 : 0,
    fiftyTwoWeekHigh: high52,
    fiftyTwoWeekLow: low52,
    // MKTCAP-CONSISTENCY FIX (Jun 2026): derive marketCap from the resolved (filed)
    // share count × price, not FMP's raw mktCap. FMP's raw mktCap = float_shares ×
    // price; if the float count was corrupted, raw mktCap is wrong too AND would
    // re-corrupt shares via the SHARES-LOCK-2 market-cap gate. shares × price closes
    // that loop and keeps marketCap consistent with sharesOutstanding.
    marketCap: (shares * price) / 1000000,
    bookEquity: bookEquityValue / 1000000,
    dividend: dividendYieldPercent,
    quarterlyDividend: lastDiv / 4,
    retentionRate: retentionRate,
    sharesOutstanding: shares / 1000000,
    netDebt: calculatedNetDebt / 1000000,
    lastYearRevenue:
      toUsd(income.revenue || parseFloat(avIncome.totalRevenue) || 0) / 1000000,
    lastYearFcf: calculatedFcf / 1000000,
    buybacks: toUsd(cashFlow.commonStockRepurchased || 0) / 1000000,
    avgBuybackYield: (() => {
      const cfHistory = data.cashFlow || [];
      if (cfHistory.length === 0 || !price || !mktCap) return 0;
      const totalBuybacks = cfHistory.reduce(
        (acc: number, curr: any) =>
          acc + (Math.abs(curr.commonStockRepurchased) || 0),
        0,
      );
      const avgBuyback = totalBuybacks / cfHistory.length;
      return (toUsd(avgBuyback) / mktCap) * 100;
    })(),
    analystTargetPrice: parseFloat(avOverview.AnalystTargetPrice) || 0,
    aftermarketPrice:
      data.aftermarket?.askPrice || data.aftermarket?.bidPrice || 0,
    aftermarketChange:
      data.aftermarket?.askPrice && price
        ? data.aftermarket.askPrice - price
        : 0,
    pricePerformance: data.priceChange || null,
    spyPerformance: data.spyPriceChange || null,
  };

  // Growth calculation remains the same (ratio based)
  const incomeHistory = data.income || [];
  let growthRate = 0;
  if (incomeHistory.length >= 3) {
    const latest = incomeHistory[0].netIncome || 0;
    const oldest = incomeHistory[incomeHistory.length - 1].netIncome || 0;
    const years = incomeHistory.length - 1;
    if (latest > 0 && oldest > 0) {
      growthRate = (Math.pow(latest / oldest, 1 / years) - 1) * 100;
    }
  }
  if (growthRate === 0) {
    growthRate =
      (parseFloat(avOverview.QuarterlyEarningsGrowthYOY) ||
        parseFloat(avOverview.QuarterlyRevenueGrowthYOY) ||
        0) * 100;
  }

  // Synthetic DCF calculation fallback
  const wacc = 0.09;
  const g = Math.min(Math.max(growthRate / 100, 0.02), 0.08);
  const syntheticDcfVal = (calculatedFcf * (1 + g)) / (wacc - g);
  const syntheticIvps = (syntheticDcfVal - calculatedNetDebt) / (shares || 1);
  const finalSyntheticDcf = Math.max(syntheticIvps, 0);

  const fmpDcfValue = {
    dcf:
      toUsd(parseFloat(data.dcf?.dcf || data.dcf?.equityValuePerShare)) ||
      finalSyntheticDcf,
    leveredDcf:
      toUsd(
        parseFloat(
          data.leveredDcf?.dcf || data.leveredDcf?.equityValuePerShare,
        ),
      ) || finalSyntheticDcf * 1.05,
    customDcf:
      toUsd(
        parseFloat(data.customDcf?.dcf || data.customDcf?.equityValuePerShare),
      ) || 0,
    customLeveredDcf:
      toUsd(
        parseFloat(
          data.customLeveredDcf?.dcf ||
            data.customLeveredDcf?.equityValuePerShare,
        ),
      ) || 0,
    customDcfData: data.customDcf || null,
    customLeveredDcfData: data.customLeveredDcf || null,
    isSynthetic: !data.dcf?.dcf && !data.leveredDcf?.dcf,
  };

  const growthData = {
    revenueGrowth:
      parseFloat(
        data.incomeGrowth?.growthRevenue || data.incomeGrowth?.revenueGrowth,
      ) ||
      parseFloat(avOverview.QuarterlyRevenueGrowthYOY) ||
      0,
    netIncomeGrowth:
      parseFloat(
        data.incomeGrowth?.growthNetIncome ||
          data.incomeGrowth?.netIncomeGrowth,
      ) ||
      parseFloat(avOverview.QuarterlyEarningsGrowthYOY) ||
      0,
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // DETERMINISTIC HISTORICAL METRICS
  // Pre-computed to replace AI estimation for structural trend assessments
  // ─────────────────────────────────────────────────────────────────────────────

  // Revenue 5-year CAGR: use up to 5 years of data (index 0 = newest, index 4 = 5yr ago)
  // Formula: (revenue_year0 / revenue_year4)^(1/4) - 1
  // Returns decimal e.g. 0.08 for 8%. Returns 0 if insufficient data.
  const revenueGrowth5Yr = (() => {
    const arr = (data.income || []).filter((r: any) => r.revenue > 0);
    if (arr.length < 2) return 0;
    const recent = arr[0].revenue;
    const base = arr[Math.min(4, arr.length - 1)].revenue;
    const years = Math.min(4, arr.length - 1);
    if (recent <= 0 || base <= 0 || years === 0) return 0;
    return Math.pow(recent / base, 1 / years) - 1;
  })();

  // FCF 3-year average YoY growth rate.
  // Computes YoY growth for each of the last 3 year-pairs, then averages.
  // Returns decimal e.g. 0.12 for 12%. Returns 0 if insufficient data.
  const avgFcfGrowth3Yr = (() => {
    const arr = (data.cashFlow || []).filter(
      (r: any) => r.freeCashFlow != null,
    );
    if (arr.length < 2) return 0;
    const growthRates: number[] = [];
    for (let i = 0; i < Math.min(3, arr.length - 1); i++) {
      const curr = arr[i].freeCashFlow;
      const prev = arr[i + 1].freeCashFlow;
      if (prev !== 0 && prev != null && curr != null) {
        growthRates.push((curr - prev) / Math.abs(prev));
      }
    }
    if (growthRates.length === 0) return 0;
    return growthRates.reduce((a, b) => a + b, 0) / growthRates.length;
  })();

  // True if freeCashFlow > 0 for each of the last 5 years (or all available if < 5).
  // Uses up to 5 most recent cashflow records.
  const hasPositiveFcfStreak5Yr = (() => {
    const arr = (data.cashFlow || []).slice(0, 5);
    if (arr.length === 0) return false;
    return arr.every((r: any) => (r.freeCashFlow ?? 0) > 0);
  })();

  // 5-year average ROIC using the same formula as the single-year ROIC above.
  // Note: uses same 21% implied tax assumption (0.79 multiplier) as single-year ROIC.
  // NOPAT = operatingIncome * 0.79
  // InvestedCapital = totalDebt + totalStockholdersEquity - cashAndCashEquivalents
  // ROIC = NOPAT / InvestedCapital
  // Returns decimal e.g. 0.14 for 14%. Returns 0 if insufficient data.
  const roicsSeries5Yr = (() => {
    const incArr = (data.income || []).slice(0, 5);
    const balArr = (data.balance || []).slice(0, 5);
    if (incArr.length === 0) return [];
    const roics: number[] = [];
    for (let i = 0; i < Math.min(incArr.length, balArr.length); i++) {
      const inc = incArr[i];
      const bal = balArr[i];
      if (!inc || !bal) continue;
      const nopat = toUsd(inc.operatingIncome || 0) * 0.79;
      const ic = toUsd(
        (bal.totalDebt || 0) +
          (bal.totalStockholdersEquity || 0) -
          (bal.cashAndCashEquivalents || 0),
      );
      if (ic > 0 && nopat > 0) roics.push(nopat / ic);
    }
    return roics;
  })();

  const avgRoic5Yr = (() => {
    if (roicsSeries5Yr.length === 0) return 0;
    return roicsSeries5Yr.reduce((a, b) => a + b, 0) / roicsSeries5Yr.length;
  })();

  // Change in gross margin from 5 years ago to most recent year, in basis points.
  // Positive = expanding, negative = compressing.
  // e.g. -350 means margin compressed 350bps over 5 years (triggers structural decline flag).
  // Returns null if insufficient data (< 2 years with valid revenue).
  const grossMarginTrend5Yr = (() => {
    const arr = (data.income || []).filter(
      (r: any) => r.revenue > 0 && r.grossProfit != null,
    );
    if (arr.length < 2) return null;
    const recentMargin = arr[0].grossProfit / arr[0].revenue;
    const baseArr = arr[Math.min(4, arr.length - 1)];
    const baseMargin = baseArr.grossProfit / baseArr.revenue;
    return Math.round((recentMargin - baseMargin) * 10000); // basis points
  })();

  // Same as grossMarginTrend5Yr but using operatingIncome.
  const operatingMarginTrend5Yr = (() => {
    const arr = (data.income || []).filter(
      (r: any) => r.revenue > 0 && r.operatingIncome != null,
    );
    if (arr.length < 2) return null;
    const recentMargin = arr[0].operatingIncome / arr[0].revenue;
    const baseArr = arr[Math.min(4, arr.length - 1)];
    const baseMargin = baseArr.operatingIncome / baseArr.revenue;
    return Math.round((recentMargin - baseMargin) * 10000); // basis points
  })();

  // Interest coverage: EBIT / interestExpense (most recent year)
  // Returns null if interestExpense is zero or missing (common for Type 4 entities)
  const interestCoverageRatio = (() => {
    const arr = (data.income || []).filter(
      (r: any) => r.operatingIncome != null,
    );
    if (arr.length === 0) return null;
    const latestIncome = arr[0];
    const ebit = toUsd(latestIncome.operatingIncome || 0);
    const intExp = toUsd(Math.abs(latestIncome.interestExpense || 0));
    if (intExp <= 0) return null; // Type 4 entities and debt-free companies
    return parseFloat((ebit / intExp).toFixed(1));
  })();

  // FMP pre-calculated owner earnings, normalized to USD millions for an apples-to-apples
  // comparison against the synthetic Base Case. Cross-check only — never the DCF anchor.
  const fmpOwnerEarnings =
    toUsd(data.fmpOwnerEarnings?.ownersEarnings || 0) / 1000000;
  const hasFmpCrossCheck = Math.abs(fmpOwnerEarnings) > 0;
  const syntheticBaseOe = syntheticOwnerEarnings / 1000000;
  const fmpVsSyntheticDivergencePct =
    hasFmpCrossCheck && Math.abs(fmpOwnerEarnings) > 0
      ? ((syntheticBaseOe - fmpOwnerEarnings) / Math.abs(fmpOwnerEarnings)) *
        100
      : 0;

  const da = cashFlowQuarterly.length > 0
    ? toUsd(cashFlowQuarterly.reduce((acc: number, curr: any) => acc + (curr.depreciationAndAmortization || 0), 0)) * (4 / cashFlowQuarterly.length) / 1000000
    : 0;

  const bookedCapex = cashFlowQuarterly.length > 0
    ? Math.abs(toUsd(cashFlowQuarterly.reduce((acc: number, curr: any) => acc + (curr.capitalExpenditure || 0), 0)) * (4 / cashFlowQuarterly.length)) / 1000000
    : 0;

  const ocf = cashFlowQuarterly.length > 0
    ? toUsd(cashFlowQuarterly.reduce((acc: number, curr: any) => acc + (curr.operatingCashFlow || curr.netCashProvidedByOperatingActivities || 0), 0)) * (4 / cashFlowQuarterly.length) / 1000000
    : 0;

  const oeAnchorUsed = syntheticBaseOe;
  const daToBookedCapexRatio = bookedCapex > 0 ? parseFloat((da / bookedCapex).toFixed(2)) : null;
  // (Tuning v2.94 F-10) SEMANTICS NOTE — "oeMaintCapexFloored" is the maintenance-capex-
  // conservative OE CANDIDATE (OCF − D&A, i.e. OE recomputed as if maintenance capex = D&A),
  // NOT a floored/clamped value. It can legitimately be NEGATIVE (OCF < D&A: SOLV −344) and
  // can EXCEED the anchor (D&A < booked capex: HENKY 4,857 vs anchor 2,502). The prop name is
  // historic — kept for corpus continuity; interpret accordingly in queries.
  const oeMaintCapexFloored = ocf - da;
  const oeOverstatementRatio = oeMaintCapexFloored > 0 ? parseFloat((oeAnchorUsed / oeMaintCapexFloored).toFixed(2)) : null;
  const assetHeavySignature = daToBookedCapexRatio != null && daToBookedCapexRatio >= 2;

  console.log(
    `[OE-MAINTENANCE-CAPEX-GAP] ${ticker}: D&A=$${da.toFixed(2)}M bookedCapex=$${bookedCapex.toFixed(2)}M ratio=${daToBookedCapexRatio !== null ? daToBookedCapexRatio + 'x' : 'n/a'} oeAnchor=$${oeAnchorUsed.toFixed(2)}M oeMaintCandidate=$${oeMaintCapexFloored.toFixed(2)}M overstatement=${oeOverstatementRatio !== null ? oeOverstatementRatio + 'x' : 'n/a (candidate ≤ 0)'} assetHeavy=${assetHeavySignature} (measurement only; OE anchor unchanged)`
  );

// ─── [OE-NCI-GAP] (measurement only) ────────────────────────────────────────
// Methodology mandates NCI-adjusted Owner Earnings; the deterministic anchor
// currently uses consolidated netIncome. This measures the gap without
// changing it. FMP stable income-statement field semantics (netIncome vs
// bottomLineNetIncome vs netIncomeDeductions) are not disambiguated in the
// docs — log all candidates raw and let the corpus resolve them.
const nciIncNetIncome = typeof income.netIncome === 'number' ? income.netIncome : null;
const nciIncBottomLine = typeof income.bottomLineNetIncome === 'number' ? income.bottomLineNetIncome : null;
const nciIncDeductions = typeof income.netIncomeDeductions === 'number' ? income.netIncomeDeductions : null;
const nciBalanceMinority = typeof balance.minorityInterest === 'number' ? balance.minorityInterest : null;

let nciRatioPct: number | null = null;
if (nciIncNetIncome !== null && nciIncBottomLine !== null && nciIncBottomLine !== 0) {
  nciRatioPct = parseFloat((Math.abs(nciIncBottomLine - nciIncNetIncome) / Math.abs(nciIncBottomLine) * 100).toFixed(2));
}
const nciGapFired =
  (nciRatioPct !== null && nciRatioPct > 5) ||
  (nciRatioPct === null && nciBalanceMinority !== null && nciBalanceMinority > 0);

if (nciGapFired) {
  console.log(
    `[OE-NCI-GAP] ${ticker}: netIncome=${nciIncNetIncome} bottomLineNetIncome=${nciIncBottomLine} netIncomeDeductions=${nciIncDeductions} balanceMinorityInterest=${nciBalanceMinority} nciRatioPct=${nciRatioPct !== null ? nciRatioPct : 'null'} (measurement only; OE anchor unchanged)`
  );
}

  const ownerEarningsData = {
    ownersEarnings: syntheticOwnerEarnings / 1000000,
    ownersEarningsLow: syntheticOwnerEarningsLow / 1000000,
    ownersEarningsHigh: syntheticOwnerEarningsHigh / 1000000,
    maintenanceCapexRatioUsed: maintenanceCapexRatioBase,
    isCapitalHeavy: isCapitalHeavy,
    isSynthetic:
      !data.ownerEarnings?.ownersEarnings &&
      (!data.cashFlowQuarterly || data.cashFlowQuarterly.length === 0),
    fmpOwnerEarnings: fmpOwnerEarnings,
    hasFmpCrossCheck: hasFmpCrossCheck,
    fmpVsSyntheticDivergencePct: fmpVsSyntheticDivergencePct,
    daToBookedCapexRatio,
    oeMaintCapexFloored: parseFloat(oeMaintCapexFloored.toFixed(2)),
    oeOverstatementRatio,
    oeAssetHeavySignature: assetHeavySignature,
    nciRatioPct,
    nciGapFired,
  };

  console.log(
    `[OE-CROSS-CHECK] ${ticker}: hasFmpCrossCheck=${hasFmpCrossCheck}, fmpOwnerEarnings=${fmpOwnerEarnings.toFixed(0)}M, syntheticBase=${syntheticBaseOe.toFixed(0)}M, divergence=${fmpVsSyntheticDivergencePct.toFixed(1)}%`
  );

  // ─── CALC-1a: DETERMINISTIC FCFF (TypeScript-owned; LLM never computes this) ──
  // FCFF_y = operatingIncome × (1 − effTax) + D&A − |capex| + changeInWorkingCapital
  // Inputs are already-fetched annual income + cash-flow statements (zero new API calls).
  // Units: millions, matching ownerEarningsData.ownersEarnings.
  const fcffData = (() => {
    const incArr = data.income || [];
    const cfArr = data.cashFlow || [];
    const n = Math.min(incArr.length, cfArr.length, 3);
    const perYear: number[] = [];
    let latestTaxRate: number | null = null;
    let fallbackUsed = false;
    for (let i = 0; i < n; i++) {
      const inc = incArr[i] || {};
      const cf = cfArr[i] || {};
      const opInc = toUsd(inc.operatingIncome);
      const dna = toUsd(cf.depreciationAndAmortization);
      const capexRaw = toUsd(cf.capitalExpenditure);
      const cwc = toUsd(cf.changeInWorkingCapital);
      // r1: a year is valid only if the core inputs actually exist.
      // operatingIncome and capitalExpenditure are load-bearing; D&A and ΔNWC
      // may legitimately be zero/absent and remain zero-filled.
      if (inc.operatingIncome == null || cf.capitalExpenditure == null) {
        console.log(`[FCFF-BASE] skipping year index ${i}: missing operatingIncome or capitalExpenditure`);
        continue;
      }
      const pretax = toUsd(inc.incomeBeforeTax);
      const taxExp = toUsd(inc.incomeTaxExpense);
      let effTax: number;
      if (pretax > 0 && inc.incomeBeforeTax != null && inc.incomeTaxExpense != null) {
        effTax = Math.min(0.35, Math.max(0, taxExp / pretax));
      } else {
        effTax = 0.21;
        fallbackUsed = true;
      }
      if (i === 0) latestTaxRate = effTax;
      const fcffY = opInc * (1 - effTax) + dna - Math.abs(capexRaw) + cwc;
      perYear.push(fcffY);
    }
    if (perYear.length === 0) {
      console.log(`[FCFF-BASE] ${ticker}: insufficient inputs — hasFcff=false`);
      return {
        hasFcff: false,
        fcffBase: null as number | null,
        fcffLatest: null as number | null,
        yearsUsed: 0,
        effectiveTaxRate: null as number | null,
        taxRateFallbackUsed: fallbackUsed,
      };
    }
    const avg = perYear.reduce((a, b) => a + b, 0) / perYear.length;
    const fcffBase = avg / 1000000;
    const fcffLatest = perYear[0] / 1000000;
    console.log(
      `[FCFF-BASE] ${ticker}: fcffBase=${fcffBase.toFixed(2)}M (yearsUsed=${perYear.length}, taxRate=${latestTaxRate != null ? latestTaxRate.toFixed(3) : "n/a"}${fallbackUsed ? ", fallback 21%" : ""}), ownerEarningsBase=${ownerEarningsData.ownersEarnings.toFixed(2)}M`,
    );
    return {
      hasFcff: true,
      fcffBase,
      fcffLatest,
      yearsUsed: perYear.length,
      effectiveTaxRate: latestTaxRate,
      taxRateFallbackUsed: fallbackUsed,
    };
  })();

  // ─── CALC-8a: DETERMINISTIC NORMALIZATION ANCHORS (TypeScript-owned) ──────
  // Method A (mid-cycle average owner earnings) and Method B (median-margin
  // reconstruction) inputs are computed here over up to 10 annual years.
  // Per-year OE reuses the synthetic base formula: netIncome + D&A − maintCapex,
  // with maintenanceCapexRatioBase applied per year. Units: millions.
  const normalizationAnchors = (() => {
    const incArr = data.income || [];
    const cfArr = data.cashFlow || [];
    const n = Math.min(incArr.length, cfArr.length, 10);
    const oeSeries: Array<{ year: string; oe: number }> = [];
    const opMargins: number[] = [];
    const grossMargins: number[] = [];
    for (let i = 0; i < n; i++) {
      const inc = incArr[i] || {};
      const cf = cfArr[i] || {};
      // Year-validity guard (CALC-1a-r1 style): load-bearing fields must exist.
      if (
        inc.netIncome == null ||
        cf.depreciationAndAmortization == null ||
        cf.capitalExpenditure == null
      ) {
        console.log(`[NORM-ANCHORS] skipping year index ${i}: missing netIncome, D&A, or capex`);
        continue;
      }
      const ni = toUsd(inc.netIncome);
      const dna = toUsd(cf.depreciationAndAmortization);
      const capex = Math.abs(toUsd(cf.capitalExpenditure));
      const oeY = ni + dna - capex * maintenanceCapexRatioBase;
      const yr = String(inc.calendarYear || inc.date || `idx${i}`);
      oeSeries.push({ year: yr, oe: oeY / 1000000 });
      const rev = toUsd(inc.revenue);
      if (rev > 0 && inc.operatingIncome != null) {
        opMargins.push((toUsd(inc.operatingIncome) / rev) * 100);
      }
      if (rev > 0 && inc.grossProfit != null) {
        grossMargins.push((toUsd(inc.grossProfit) / rev) * 100);
      }
    }
    const median = (arr: number[]): number | null => {
      if (arr.length === 0) return null;
      const a = [...arr].sort((x, y) => x - y);
      const m = Math.floor(a.length / 2);
      return a.length % 2 ? a[m] : (a[m - 1] + a[m]) / 2;
    };
    const midCycleAvgOe =
      oeSeries.length > 0
        ? oeSeries.reduce((acc, r) => acc + r.oe, 0) / oeSeries.length
        : null;
    const medianOperatingMarginPct = median(opMargins);
    const medianGrossMarginPct = median(grossMargins);
    // Method B candidate: median operating margin × trailing revenue + latest D&A − maintenance capex.
    const trailingRevenue = toUsd((incArr[0] || {}).revenue);
    const latestDna = toUsd((cfArr[0] || {}).depreciationAndAmortization);
    const latestCapex = Math.abs(toUsd((cfArr[0] || {}).capitalExpenditure));
    const methodBCandidateOe =
      medianOperatingMarginPct != null && trailingRevenue > 0
        ? ((medianOperatingMarginPct / 100) * trailingRevenue +
            latestDna -
            latestCapex * maintenanceCapexRatioBase) /
          1000000
        : null;
    console.log(
      `[NORM-ANCHORS] ${ticker}: yearsUsed=${oeSeries.length}, ` +
        `midCycleAvgOe=${midCycleAvgOe != null ? midCycleAvgOe.toFixed(0) : "n/a"}M, ` +
        `medianOpMargin=${medianOperatingMarginPct != null ? medianOperatingMarginPct.toFixed(1) : "n/a"}%, ` +
        `methodBCandidate=${methodBCandidateOe != null ? methodBCandidateOe.toFixed(0) : "n/a"}M, ` +
        `ttmOeBase=${(syntheticOwnerEarnings / 1000000).toFixed(0)}M`,
    );
    // Measurement (Tuning v2.59 §36.7): divergence of each normalization anchor from the TTM/
    // synthetic OE base, so the corpus shows how often Method A (simple 10-yr mean, growth-lag-biased)
    // vs Method B (median margin × trailing revenue, trend-aware) diverge — and on which sectors.
    const ttmOeBaseM = syntheticOwnerEarnings / 1000000;
    const midCycleVsTtmDivergencePct =
      midCycleAvgOe != null && ttmOeBaseM !== 0
        ? ((midCycleAvgOe - ttmOeBaseM) / Math.abs(ttmOeBaseM)) * 100
        : null;
    const methodBVsTtmDivergencePct =
      methodBCandidateOe != null && ttmOeBaseM !== 0
        ? ((methodBCandidateOe - ttmOeBaseM) / Math.abs(ttmOeBaseM)) * 100
        : null;
    // [S2] Anchor-basis measurement (compounder-labeling gap). oeMidCycleDivergencePct
    // uses the spec's basis — divergence of the TTM anchor FROM the mid-cycle average
    // (ATAT ≈ +170%), distinct from midCycleVsTtmDivergencePct above. oeFmpDivergencePct
    // promotes the existing synthetic-vs-FMP cross-check divergence to a first-class prop.
    //
    // [F1] Provisional default; the AUTHORITATIVE basis is resolved at the DCF-input
    // site in agentPipeline once the LLM-final normalizedOwnerEarnings is known
    // (see [OE-ANCHOR-BASIS]). Kept defined so any pre-DCF reader is safe.
    const oeAnchorBasis = "ttm";
    const oeMidCycleDivergencePct =
      midCycleAvgOe != null && midCycleAvgOe !== 0
        ? ((ttmOeBaseM - midCycleAvgOe) / Math.abs(midCycleAvgOe)) * 100
        : null;
    const oeFmpDivergencePct = hasFmpCrossCheck
      ? fmpVsSyntheticDivergencePct
      : null;
    return {
      hasAnchors: oeSeries.length >= 3,
      oeSeries,
      midCycleAvgOe,
      midCycleYearsUsed: oeSeries.length,
      medianOperatingMarginPct,
      medianGrossMarginPct,
      methodBCandidateOe,
      midCycleVsTtmDivergencePct,
      methodBVsTtmDivergencePct,
      // [F1] TTM candidate exposed so the DCF-input site can resolve the
      // authoritative anchor basis against the full {ttm, midCycle, methodB} set.
      ttmOeBaseM,
      oeAnchorBasis,
      oeMidCycleDivergencePct,
      oeFmpDivergencePct,
    };
  })();

  // [SPEC-2] Deterministic Type-4 investment composition. Sourced from the ALREADY
  // fetched balance sheet (zero new FMP calls) and currency-normalized via toUsd —
  // computed here rather than at the agentPipeline t4Bs classification block because
  // toUsd is only in scope in this function, and an unconverted figure would render a
  // non-USD filer's reporting-currency magnitude as dollars.
  //
  // The memo must state investment-engine size from THIS, not from the Historian's
  // free-text investmentPortfolioSize: BRK-B's memo called ~$658B an "equity
  // portfolio" when that magnitude is TOTAL investments (incl. the ~$400B cash/T-bill
  // pile); the equity book is ~$263B (13F) / ~$312B incl. the Japanese trading houses.
  // Components are null when the balance sheet does not carry them — never synthesized.
  // Units: $MM, matching ownerEarningsData.ownersEarnings.
  const type4InvestmentComposition = (() => {
    if (entityType !== "Type 4") return null;
    const pick = (v: any) =>
      typeof v === "number" && !isNaN(v) ? toUsd(v) / 1000000 : null;
    const totalInvestments = pick(balance.totalInvestments);
    const longTermEquityAndOther = pick(balance.longTermInvestments);
    const cashAndShortTerm = pick(balance.cashAndShortTermInvestments);
    if (
      totalInvestments == null &&
      longTermEquityAndOther == null &&
      cashAndShortTerm == null
    ) {
      return null;
    }
    console.log(
      `[TYPE4-INVESTMENT-COMPOSITION] ${ticker}: totalInvestments=${totalInvestments != null ? totalInvestments.toFixed(0) + "M" : "n/a"}, ` +
        `longTermEquityAndOther=${longTermEquityAndOther != null ? longTermEquityAndOther.toFixed(0) + "M" : "n/a"}, ` +
        `cashAndShortTerm=${cashAndShortTerm != null ? cashAndShortTerm.toFixed(0) + "M" : "n/a"}`,
    );
    return { totalInvestments, longTermEquityAndOther, cashAndShortTerm };
  })();

  // TYPE 4 Financial Entity Valuation Inputs
  const type4Valuation = (() => {
    if (entityType !== "Type 4") return null;

    let insufficientHistory = false;
    let tbvSource = "";
    let tbvPerShare = 0;

    const latestMetrics =
      data.metricsHistory && data.metricsHistory.length > 0
        ? data.metricsHistory[0]
        : data.metrics || {};
    // PRIMARY: key-metrics tangibleAssetValue is the reliably-present field for financials
    // (stable/key-metrics has NO tangibleBookValuePerShare). Raw $ / raw shares = $/share.
    if (latestMetrics.tangibleAssetValue != null && shares > 0) {
      tbvPerShare = latestMetrics.tangibleAssetValue / shares;
      tbvSource = "stable/key-metrics tangibleAssetValue / shares";
    } else if (latestMetrics.tangibleBookValuePerShare != null) {
      tbvPerShare = latestMetrics.tangibleBookValuePerShare;
      tbvSource = "stable/key-metrics tangibleBookValuePerShare";
    } else {
      const sharesOut = shares; // using the shares computed above
      const stockholdersEquity = balance.totalStockholdersEquity || 0;
      const goodwill = balance.goodwill || 0;
      const intangibles = balance.intangibleAssets || 0;
      if (sharesOut > 0 && stockholdersEquity > 0) {
        tbvPerShare = (stockholdersEquity - goodwill - intangibles) / sharesOut;
        tbvSource =
          "fallback: (Total Stockholders Equity - Goodwill - Intangible Assets) / sharesOut";
      }
    }
    // GUARD: the data exists for real financials; a non-positive TBV here means every source
    // missed. Do NOT let a ~0 TBV flow into the P/TBV terminal and mint a $0.02 IV — flag it so
    // the downstream IV-coherence path can suppress the valuation instead.
    if (!(tbvPerShare > 0)) {
      tbvSource = "UNAVAILABLE";
      console.warn(
        `[TYPE4-TBV-UNAVAILABLE] ${ticker}: tbvPerShare resolved to ${tbvPerShare}; all sources empty ` +
          `(tangibleAssetValue=${latestMetrics.tangibleAssetValue}, equity=${balance.totalStockholdersEquity}). ` +
          `IV must be suppressed, not minted from a near-zero TBV.`,
      );
    }

    const metricsArr = data.metricsHistory || [];
    let ptbvArray: number[] = [];
    let ptbvMedianSource: "computed-historical-keymetrics" | "computed-current-only" | "none" = "none";

    // TIER 1 — historical P/TBV = period-end marketCap / tangibleAssetValue, per year (ZERO calls).
    // Only if STEP 0 confirmed marketCap VARIES across rows (period-end) and tangibleAssetValue is
    // tangible book. Gate on both being positive.
    for (let i = 0; i < Math.min(10, metricsArr.length); i++) {
      const mc = Number(metricsArr[i]?.marketCap);
      const tav = Number(metricsArr[i]?.tangibleAssetValue);
      if (isFinite(mc) && mc > 0 && isFinite(tav) && tav > 0) {
        ptbvArray.push(mc / tav);
      }
    }
    if (ptbvArray.length >= 5) ptbvMedianSource = "computed-historical-keymetrics";

    // TIER 2 — current-only fallback (ZERO calls): current price / current tangible BVPS.
    // Not a history, but a valid RI cross-check anchor when Tier 1 is unavailable.
    if (ptbvArray.length < 5) {
      const bal0 = (data.balance && data.balance[0]) || {};
      const eq = Number(bal0.totalStockholdersEquity);
      const gw = Number(bal0.goodwill) || 0;
      const intang = Number(bal0.intangibleAssets) || 0;
      const sh = Number(shares); // engine shares are raw units
      const px = Number(data.quote?.price ?? data.quote?.[0]?.price);
      if (isFinite(eq) && isFinite(sh) && sh > 0 && isFinite(px) && px > 0) {
        const tbvps = (eq - gw - intang) / sh;
        if (tbvps > 0) {
          ptbvArray = [px / tbvps];
          ptbvMedianSource = "computed-current-only";
        }
      }
    }

    if (ptbvArray.length < 5) insufficientHistory = true;

    let ptbvTrough: number | null = null;
    let ptbvMedian: number | null = null;
    let ptbvPeak: number | null = null;

    if (ptbvArray.length > 0) {
      ptbvArray.sort((a, b) => a - b);
      ptbvTrough = ptbvArray[0];
      ptbvPeak = ptbvArray[ptbvArray.length - 1];
      const mid = Math.floor(ptbvArray.length / 2);
      ptbvMedian =
        ptbvArray.length % 2 !== 0
          ? ptbvArray[mid]
          : (ptbvArray[mid - 1] + ptbvArray[mid]) / 2;
    }

    // [PTBV-DIAG] (Tuning §54.5 / §56) measurement-only: explains why Tier 1 (historical) resolved
    // vs fell back to Tier 2 (current-only). Recomputes the Tier-1 point count + drop reasons from
    // the SAME already-fetched metricsArr (ZERO new FMP calls); logging only — no logic/branch/return
    // change. Tier-2 reassigns ptbvArray, so the true Tier-1 count is recomputed here, not read off it.
    {
      const __diagRows = metricsArr.slice(0, 10);
      const __finiteMc = __diagRows
        .map((r: any) => Number(r?.marketCap))
        .filter((v: number) => isFinite(v) && v > 0);
      const __tier1Points = __diagRows.filter((r: any) => {
        const mc = Number(r?.marketCap);
        const tav = Number(r?.tangibleAssetValue);
        return isFinite(mc) && mc > 0 && isFinite(tav) && tav > 0;
      }).length;
      const __dropMissingMktCap = __diagRows.filter(
        (r: any) => !(isFinite(Number(r?.marketCap)) && Number(r?.marketCap) > 0),
      ).length;
      const __dropMissingTav = __diagRows.filter(
        (r: any) => !(isFinite(Number(r?.tangibleAssetValue)) && Number(r?.tangibleAssetValue) > 0),
      ).length;
      const __mktCapVaries = new Set(__finiteMc).size > 1;
      console.log(
        `[PTBV-DIAG] ${ticker}: kmRows=${metricsArr.length}, tier1Points=${__tier1Points} (need>=5), ` +
          `dropMissingMktCap=${__dropMissingMktCap}, dropMissingTav=${__dropMissingTav}, ` +
          `mktCapVaries=${__mktCapVaries}, ptbvMedianSource=${ptbvMedianSource}, ` +
          `ptbvMedian=${ptbvMedian != null ? ptbvMedian.toFixed(4) : "null"}`,
      );
    }

    // ─── F13 (Tuning v2.66 §41) + F16 (Tuning v2.78 §45): Type-4 terminal ROTCE sourcing ──
    // The general roicsSeries5Yr derives NOPAT from income.operatingIncome. For holding-company
    // insurers (BRK.B and peers) FMP's normalized operatingIncome is null/0 — they report
    // investment gains + non-GAAP "operating earnings", not a clean operating line — so most years
    // fail the (ic>0 && nopat>0) guard. F13 handled the TOTAL-failure case (series empty →
    // avgRoic5Yr=0 → rotceBase=0 → P/TBV terminal collapses). But BRK.B exposed the PARTIAL case:
    // exactly ONE usable operatingIncome year (opIncomeSeriesLen=1) → avgRoic5Yr=5.78% > 0, so
    // F13's `!(rotceBase>0)` guard never fired and the terminal compounded book at a degenerate
    // single-year 5.78% instead of the ~10.4% deterministic 5-yr key-metrics ROTCE shown in the
    // same memo. F16 broadens the sourcing: compute the key-metrics ROE 5-yr average as a candidate
    // ALWAYS, and prefer it whenever the operatingIncome series is too SHORT to be a through-cycle
    // base (<3 usable years) OR the operatingIncome ROTCE materially understates the key-metrics ROE
    // (<60% of it). ROE ≈ ROTCE for financials whose tangible equity ≈ common equity. Reuses the
    // ALREADY-FETCHED stable/key-metrics array (data.metricsHistory, field `returnOnEquity`/`roe`,
    // confirmed against FMP docs) — ZERO new FMP calls; deterministic trusted-source values; the
    // LLM does no math. Runs only inside the Type-4 type4Valuation IIFE; non-Type-4 unaffected.
    //
    // GATE: the behavioral SWITCH is verdict-moving AND coupled to §42 (the P/TBV terminal
    // double-count — fixing ROTCE up removes the error that currently offsets the double-count, so
    // shipping the switch alone over-states IV). Per the RF-3/RF-3b precedent, the SWITCH is gated
    // behind TYPE4_ROTCE_5YR_ENABLED (default off): with the flag off, the live rotceBase is
    // UNCHANGED (still the operatingIncome value — verdict identical to today) but the measurement
    // records both candidates and what the switch WOULD choose, so the corpus accumulates while
    // Trey ratifies the §42 terminal-formula decision. Flip the flag together with the §42 fix.
    const rotceOpIncomeCandidate = Math.max(0, avgRoic5Yr);

    // Key-metrics 5yr ROE candidate — computed unconditionally (both the F13 total-failure fallback
    // and the F16 short/divergent switch consume it). Single source for the ROE field logic.
    const roeSeries: number[] = [];
    for (let i = 0; i < Math.min(5, metricsArr.length); i++) {
      const m = metricsArr[i] || {};
      const raw =
        typeof m.returnOnEquity === "number"
          ? m.returnOnEquity
          : typeof m.roe === "number"
            ? m.roe
            : null;
      // FMP returns ROE as a decimal (e.g. 0.092). Guard against a stray percent-scaled value.
      if (raw != null && isFinite(raw) && raw > 0) {
        roeSeries.push(raw > 1.5 ? raw / 100 : raw);
      }
    }
    const roe5yrKeyMetrics =
      roeSeries.length > 0
        ? Math.max(0, roeSeries.reduce((a, b) => a + b, 0) / roeSeries.length)
        : null;

    // Divergence of the opIncome candidate vs the key-metrics ROE (relative, signed toward
    // understatement). null when the ROE candidate is unavailable.
    const rotceDivergencePct =
      roe5yrKeyMetrics != null && roe5yrKeyMetrics > 0
        ? Number(
            (
              ((rotceOpIncomeCandidate - roe5yrKeyMetrics) / roe5yrKeyMetrics) *
              100
            ).toFixed(2),
          )
        : null;

    // Decide the preferred source (measurement — always computed regardless of the flag):
    //  - "operatingIncome/IC"          usable multi-year opIncome series that agrees with ROE
    //  - "key-metrics 5yr (short)"     opIncome series too short (<3 usable years)
    //  - "key-metrics 5yr (divergent)" opIncome ROTCE < 60% of the key-metrics ROE (understates)
    //  - "key-metrics ROE fallback"    opIncome series empty/zero (the F13 total-failure case)
    //  - "none"                        neither source available
    const F16_SHORT_SERIES_MAX = 3; // <3 usable opIncome years ⇒ not a through-cycle base
    const F16_UNDERSTATE_RATIO = 0.6; // opIncome ROTCE below 60% of ROE ⇒ materially understates
    let rotceSourceChosen: string;
    if (!(rotceOpIncomeCandidate > 0)) {
      rotceSourceChosen =
        roe5yrKeyMetrics != null ? "key-metrics ROE fallback" : "none";
    } else if (
      roe5yrKeyMetrics != null &&
      roicsSeries5Yr.length < F16_SHORT_SERIES_MAX
    ) {
      rotceSourceChosen = "key-metrics 5yr (short)";
    } else if (
      roe5yrKeyMetrics != null &&
      rotceOpIncomeCandidate < F16_UNDERSTATE_RATIO * roe5yrKeyMetrics
    ) {
      rotceSourceChosen = "key-metrics 5yr (divergent)";
    } else {
      rotceSourceChosen = "operatingIncome/IC";
    }

    // Whether the chosen source differs from today's live behavior (which always uses opIncome when
    // it is > 0, and ROE only when opIncome is empty). True ⇒ the F16 switch WOULD change rotceBase.
    const rotceSwitchWouldApply =
      rotceSourceChosen === "key-metrics 5yr (short)" ||
      rotceSourceChosen === "key-metrics 5yr (divergent)";

    // The F13 total-failure fallback (opIncome empty) is a CORRECTNESS fix and remains ALWAYS ON —
    // there is no valid opIncome value to preserve, so no verdict is being changed by using ROE.
    // The F16 switch (short/divergent) is the verdict-moving part, gated behind the flag.
    const type4Rotce5yrEnabled = process.env.TYPE4_ROTCE_5YR_ENABLED === "true";
    // ─── BUG-4 FIX: disarm the dormant-flag landmine ────────────────────────────
    // For Type-4 financials the short/divergent switch is a CORRECTNESS fix, not a
    // tuning choice: operatingIncome/IC ROTCE is not a valid through-cycle base for
    // banks/insurers (opIncomeSeriesLen is typically 1). Applying it unconditionally
    // for Type-4 aligns the live rotceBase with the key-metrics ROTCE the RI
    // valuation already uses, and means flipping TYPE4_ROTCE_5YR_ENABLED can no
    // longer collapse the IV ~60%. The env flag still governs any non-Type-4 use.
    const forceRoeForType4 = entityType === "Type 4";
    const applyRoe =
      roe5yrKeyMetrics != null &&
      (rotceSourceChosen === "key-metrics ROE fallback" ||
        ((type4Rotce5yrEnabled || forceRoeForType4) && rotceSwitchWouldApply));

    let rotceBase: number;
    let rotceTrough = 0;
    let rotcePeak = 0;
    let rotceSource: string;

    if (applyRoe && roe5yrKeyMetrics != null) {
      rotceBase = roe5yrKeyMetrics;
      rotceTrough =
        roeSeries.length >= 3
          ? Math.max(0, Math.min(...roeSeries))
          : Math.max(0, roe5yrKeyMetrics - 0.025);
      rotcePeak =
        roeSeries.length >= 3
          ? Math.max(0, Math.max(...roeSeries))
          : Math.max(0, roe5yrKeyMetrics + 0.02);
      rotceSource = rotceSourceChosen;
    } else {
      rotceBase = rotceOpIncomeCandidate;
      if (roicsSeries5Yr.length >= 3) {
        rotceTrough = Math.max(0, Math.min(...roicsSeries5Yr));
        rotcePeak = Math.max(0, Math.max(...roicsSeries5Yr));
      } else {
        rotceTrough = Math.max(0, rotceBase - 0.025);
        rotcePeak = Math.max(0, rotceBase + 0.02);
      }
      rotceSource = roicsSeries5Yr.length > 0 ? "operatingIncome/IC" : "none";
    }

    console.log(
      `[TYPE4-ROTCE-SOURCE] ${ticker}: rotceBase=${(rotceBase * 100).toFixed(2)}% ` +
        `(source=${rotceSource}, opIncomeSeriesLen=${roicsSeries5Yr.length}). ` +
        `[F16] rotceOpIncome=${(rotceOpIncomeCandidate * 100).toFixed(2)}%, ` +
        `rotce5yrKeyMetrics=${roe5yrKeyMetrics != null ? (roe5yrKeyMetrics * 100).toFixed(2) + "%" : "n/a"}, ` +
        `divergencePct=${rotceDivergencePct != null ? rotceDivergencePct + "%" : "n/a"}, ` +
        `chosen=${rotceSourceChosen}, switchWouldApply=${rotceSwitchWouldApply}, ` +
        `flagEnabled=${type4Rotce5yrEnabled}, applied=${applyRoe === true && rotceSwitchWouldApply}.`,
    );
    // ─── END F13 + F16 ROTCE sourcing ──────────────────────────────────────

    return {
      tbvPerShare,
      ptbvTrough,
      ptbvMedian,
      ptbvPeak,
      ptbvMedianSource,
      ptbvMedianResolved: ptbvMedian != null && ptbvMedian > 0,
      tbvSource,
      tbvPerShareUnavailable: !(tbvPerShare > 0),
      insufficientHistory,
      rotceBase,
      rotceTrough,
      rotcePeak,
      rotceSource,
      // F16 measurement candidates (Tuning v2.78 §45) — surfaced for analysis_completed enrichment.
      rotceOpIncome: rotceOpIncomeCandidate,
      rotce5yrKeyMetrics: roe5yrKeyMetrics,
      rotceDivergencePct,
      rotceSourceChosen,
      rotceSwitchWouldApply,
      rotceSwitchApplied: applyRoe === true && rotceSwitchWouldApply,
    };
  })();

  return {
    ...baseData,
    // Type-only carrier: the caller (agentPipeline treasury loopback) assigns
    // thirtyYearTreasuryYield onto this object after construction. Spreading a
    // runtime-empty typed object declares the optional field so that post-hoc
    // assignment type-checks, without adding any own property. Zero runtime change.
    ...({} as { thirtyYearTreasuryYield?: number }),
    fmpDcf: fmpDcfValue,
    ownerEarningsData: ownerEarningsData,
    fcffData: fcffData,
    normalizationAnchors: normalizationAnchors,
    // [SPEC-2] Deterministic investment composition (Type 4 only; null otherwise).
    type4InvestmentComposition,
    isType5DataContaminated,
    type5Origin,
    type5UnvaluableReason,
    growthData: growthData,
    type4Valuation,
    historicalMetrics: {
      revenueGrowth5Yr,
      avgFcfGrowth3Yr,
      hasPositiveFcfStreak5Yr,
      avgRoic5Yr,
      grossMarginTrend5Yr: isType5Entity ? null : grossMarginTrend5Yr,
      operatingMarginTrend5Yr: isType5Entity ? null : operatingMarginTrend5Yr,
      interestCoverageRatio,
    },
    enterpriseValueData:
      data.enterpriseValues &&
      Object.keys(data.enterpriseValues).length > 0 &&
      !data.enterpriseValues["Error Message"]
        ? data.enterpriseValues
        : null,
    oracleMetrics: (() => {
      // [TYPE5-TTM-SOURCE] (Tuning 10.5/10.6) For Type 5 spin-offs the most-recent ANNUAL
      // income/balance is parent-contaminated; prefer the per-stock TTM objects (already fetched)
      // so display metrics reflect standalone performance. These consts are reused by the per-metric
      // overrides below (and by the Prompt-3 sweep). isType5Entity is defined earlier in scope.
      const ttmK = (data.keyMetricsTtm || {}) as any;
      const ttmR = (data.ratiosTtm || {}) as any;
      const t5 = isType5Entity;
      const roe =
        (t5 && typeof ttmK.returnOnEquityTTM === "number" ? ttmK.returnOnEquityTTM : 0) * 100 ||
        (metrics.roeTTM ||
          metrics.roe ||
          ratios.returnOnEquityTTM ||
          ratios.returnOnEquity ||
          ratios.roe ||
          metrics.returnOnEquity ||
          parseFloat(avOverview.ReturnOnEquityTTM) ||
          0 ||
          parseFloat(avOverview.ReturnOnEquity) ||
          0 ||
          (income.netIncome &&
          balance.totalStockholdersEquity &&
          balance.totalStockholdersEquity !== 0
            ? income.netIncome / balance.totalStockholdersEquity
            : 0) ||
          (parseFloat(avIncome.netIncome) &&
          parseFloat(avBalance.totalShareholderEquity)
            ? parseFloat(avIncome.netIncome) /
              parseFloat(avBalance.totalShareholderEquity)
            : 0)) * 100;

      const eps =
        t5 && typeof ttmR.netIncomePerShareTTM === "number"
          ? toUsd(ttmR.netIncomePerShareTTM)
          : toUsd(
              income.eps ||
                profile.eps ||
                quote.eps ||
                profile.epsTTM ||
                metrics.eps ||
                parseFloat(avOverview.EPS) ||
                0 ||
                parseFloat(avIncome.reportedEPS) ||
                0 ||
                0,
            );

      const ownerEarningsValue = Math.max(
        toUsd(data.ownerEarnings?.ownersEarnings || 0) / 1000000,
        calculatedFcf / 1000000,
      );

      const fcfPerShare =
        toUsd(
          metrics.freeCashFlowPerShareTTM ||
            metrics.freeCashFlowPerShare ||
            metrics.fcfPerShare ||
            ratios.fcfPerShare,
        ) || (shares > 0 ? calculatedFcf / shares : 0);

      const peRatio =
        // [TTM-PE-FIX 10.9] Trailing P/E from ratiosTtm (data.ratiosTtm) for ALL entities.
        // The former ratios.priceEarningsRatioTTM / ratios.peRatioTTM reads hit the ANNUAL
        // ratios object, where *TTM fields do not exist, so they were dead and the chain fell
        // through to quote.pe. The ttmR read is correct for every entity, so the t5 gate is removed.
        (typeof ttmR.priceToEarningsRatioTTM === "number" ? ttmR.priceToEarningsRatioTTM : 0) ||
        ratios.priceEarningsRatio ||
        ratios.peRatio ||
        quote.pe ||
        quote.peRatio ||
        profile.pe ||
        profile.peRatio ||
        metrics.peRatio ||
        parseFloat(avOverview.PERatio) ||
        0 ||
        parseFloat(avOverview.TrailingPE) ||
        0 ||
        (price && eps && eps > 0 ? price / eps : 0);

      const ebit = toUsd(
        income.operatingIncome ||
          income.netIncome +
            (income.interestExpense || 0) +
            (income.incomeTaxExpense || 0) ||
          metrics.ebit ||
          0,
      );
      const nopat = ebit * 0.79;
      const investedCapital = toUsd(
        (balance.totalDebt || 0) +
          (balance.totalStockholdersEquity || 0) -
          (balance.cashAndCashEquivalents || 0),
      );
      const annualRoic =
        investedCapital > 0
          ? (nopat / investedCapital) * 100
          : (metrics.returnOnInvestedCapital || metrics.roicTTM || metrics.roic || ratios.roic || 0) * 100;
      // ─── BUG-3 FIX: peer-table ROIC for Type-4 financials ───────────────────────
      // NOPAT/InvestedCapital is meaningless for banks/insurers and produced the
      // in-report contradiction (e.g. BRK-B shown at 5.8% in the peer table while
      // the valuation and unit economics used 14.5%). For Type-4 entities surface
      // the tangible-common ROTCE (≈ROE), the same basis the Type-4 valuation uses.
      const roic =
        entityType === "Type 4"
          ? roe
          : t5 && typeof ttmK.returnOnInvestedCapitalTTM === "number"
            ? ttmK.returnOnInvestedCapitalTTM * 100
            : annualRoic;
      if (t5 && typeof ttmK.returnOnInvestedCapitalTTM === "number") {
        console.log(
          `[TYPE5-TTM-SOURCE] ${ticker}: ROIC annual ${annualRoic.toFixed(2)}% → TTM ${(ttmK.returnOnInvestedCapitalTTM * 100).toFixed(2)}% (contamination delta ${(annualRoic - ttmK.returnOnInvestedCapitalTTM * 100).toFixed(2)}pp).`,
        );
      }

      const pegRatio = t5
        ? null
        : ratios.pegRatioTTM ||
          ratios.pegRatio ||
          parseFloat(avOverview.PEGRatio) ||
          0 ||
          (peRatio && growthRate > 0 ? peRatio / growthRate : 0);

      const estimates = data.estimates || {};
      const rawEpsForward =
        estimates.estimatedEpsAvg ??
        estimates.epsAvg ??
        (avOverview.EPS
          ? parseFloat(avOverview.EPS) * (1 + growthRate / 100)
          : null);
      const epsForward =
        rawEpsForward != null && !isNaN(rawEpsForward)
          ? toUsd(rawEpsForward)
          : null;
      const peForward =
        (avOverview.ForwardPE ? parseFloat(avOverview.ForwardPE) : null) ??
        (price && epsForward && epsForward > 0 ? price / epsForward : null);

      const fcfYield =
        price > 0 && fcfPerShare > 0 ? (fcfPerShare / price) * 100 : 0;
      const forwardFcfYield =
        price > 0 && fcfPerShare > 0
          ? ((fcfPerShare * (1 + growthRate / 100)) / price) * 100
          : 0;

      const operatingMargin =
        t5 && typeof ttmR.operatingProfitMarginTTM === "number"
          ? ttmR.operatingProfitMarginTTM * 100
          : income.operatingIncome != null && income.revenue
            ? (income.operatingIncome / income.revenue) * 100
            : null;
      const cashConversion =
        t5 && typeof ttmR.freeCashFlowPerShareTTM === "number" && typeof ttmR.netIncomePerShareTTM === "number" && ttmR.netIncomePerShareTTM !== 0
          ? (ttmR.freeCashFlowPerShareTTM / ttmR.netIncomePerShareTTM) * 100
          : eps && eps !== 0
            ? (fcfPerShare / eps) * 100
            : null;
      const interestCoverage =
        t5 && typeof ttmR.interestCoverageRatioTTM === "number" && ttmR.interestCoverageRatioTTM > 0
          ? ttmR.interestCoverageRatioTTM
          : income.interestExpense && toUsd(income.interestExpense) > 0
            ? ebit / toUsd(income.interestExpense)
            : null;

      return {
        roe,
        fcf: calculatedFcf / 1000000,
        fcfPerShare: fcfPerShare,
        eps,
        ownerEarnings: ownerEarningsValue,
        debtToEquity:
          metrics.debtToEquityTTM ||
          metrics.debtToEquity ||
          metrics.debtEquityRatio ||
          (balance.totalDebt && balance.totalStockholdersEquity
            ? balance.totalDebt / balance.totalStockholdersEquity
            : 0),
        peRatio,
        pegRatio,
        pbRatio:
          ratios.priceToBookRatioTTM ||
          ratios.priceToBookRatio ||
          ratios.pbRatio ||
          0,
        debtToEbitda:
          metrics.debtToEbitdaTTM ||
          metrics.debtToEbitda ||
          metrics.debtEbitdaRatio ||
          (balance.totalDebt && income.ebitda
            ? balance.totalDebt / income.ebitda
            : 0),
        roic,
        operatingMargin,
        cashConversion,
        interestCoverage,
        epsForward,
        peForward,
        fcfYield,
        forwardFcfYield,
      };
    })(),
  };
}
export function trimInstitutionalData(data: any) {
  if (!data) return null;

  const trimObject = (obj: any, keys: string[]) => {
    if (!obj) return null;
    const trimmed: any = {};
    keys.forEach((key) => {
      if (key in obj) trimmed[key] = obj[key];
    });
    return trimmed;
  };

  return {
    profile: trimObject(data.profile, [
      "companyName",
      "price",
      "mktCap",
      "lastDiv",
      "range",
      "changes",
      "industry",
      "description",
      "ceo",
      "sector",
      "currency",
    ]),
    income: (Array.isArray(data.income) ? data.income : [])
      .slice(0, 10)
      .map((item: any) =>
        trimObject(item, [
          "date",
          "revenue",
          "grossProfit",
          "operatingIncome",
          "netIncome",
          "eps",
          "ebitda",
          "interestExpense",
          "reportedCurrency",
        ]),
      ),
    balance: (Array.isArray(data.balance) ? data.balance : [])
      .slice(0, 10)
      .map((item: any) =>
        trimObject(item, [
          "date",
          "cashAndCashEquivalents",
          "totalAssets",
          "totalLiabilities",
          "totalStockholdersEquity",
          "totalDebt",
          "netDebt",
        ]),
      ),
    cashFlow: (Array.isArray(data.cashFlow) ? data.cashFlow : [])
      .slice(0, 10)
      .map((item: any) =>
        trimObject(item, [
          "date",
          "netCashProvidedByOperatingActivities",
          "capitalExpenditure",
          "freeCashFlow",
        ]),
      ),
    metrics: trimObject(data.metrics, [
      "roeTTM",
      "netIncomePerEBTTTM",
      "freeCashFlowPerShareTTM",
      "debtToEquityTTM",
      "debtToEbitdaTTM",
      "roe",
      "netIncomePerEBT",
      "freeCashFlowPerShare",
      "debtToEquity",
      "debtToEbitda",
    ]),
    ratios: trimObject(data.ratios, [
      "priceEarningsRatioTTM",
      "priceToBookRatioTTM",
      "priceToFreeCashFlowsRatioTTM",
      "pegRatioTTM",
      "dividendYieldTTM",
      "priceEarningsRatio",
      "priceToBookRatio",
      "priceToFreeCashFlowsRatio",
      "pegRatio",
      "dividendYield",
      "dividendYieldPercentage",
      "dividendPayoutRatio",
      "ebitMargin",
      "ebitdaMargin",
      "operatingProfitMargin",
      "netProfitMargin",
      "grossProfitMargin",
      "debtToEquityRatio",
      "currentRatio",
      "quickRatio",
    ]),
    ownerEarnings: data.ownerEarnings,
    incomeGrowth: data.incomeGrowth,
    enterpriseValues: (Array.isArray(data.enterpriseValues)
      ? data.enterpriseValues
      : []
    ).slice(0, 10),
    productSegmentation: (Array.isArray(data.productSegmentation)
      ? data.productSegmentation
      : []
    ).slice(0, 5),
    geoSegmentation: (Array.isArray(data.geoSegmentation)
      ? data.geoSegmentation
      : []
    ).slice(0, 5),
    peers: (Array.isArray(data.peers) ? data.peers : []).slice(0, 5),
    insiderTrading: (Array.isArray(data.insiderTrading)
      ? data.insiderTrading
      : []
    )
      .slice(0, 5)
      .map((item: any) =>
        trimObject(item, [
          "year",
          "quarter",
          "acquiredTransactions",
          "disposedTransactions",
          "acquiredDisposedRatio",
          "totalAcquired",
          "totalDisposed",
          "totalPurchases",
          "totalSales",
        ]),
      ),
    alphaVantage: {
      overview: trimObject(data.alphaVantage?.overview, [
        "Symbol",
        "Name",
        "Description",
        "Sector",
        "Industry",
        "MarketCapitalization",
        "PERatio",
        "PEGRatio",
        "TrailingPE",
        "ForwardPE",
        "DividendYield",
        "EPS",
        "RevenueTTM",
        "ProfitMargin",
        "OperatingMarginTTM",
        "ReturnOnEquityTTM",
        "Beta",
        "52WeekHigh",
        "52WeekLow",
        "SharesOutstanding",
        "IPO",
      ]),
      quote: data.alphaVantage?.quote?.["Global Quote"],
    },
  };
}
export interface ScenarioReturnItem {
  growth: number;
  terminalMultiple: number;
  fcfYieldContribution: number;
  growthContribution: number;
  multipleChangeContribution: number;
  impliedIrr: number;
}

export interface ScenarioReturns {
  bear: ScenarioReturnItem;
  base: ScenarioReturnItem;
  bull: ScenarioReturnItem;
}

export function computeScenarioReturns(
  entryMultiple: number,
  fcfYield: number,
  horizonYears: number,
  baseGrowth: number,
  baseTerminalMultiple: number,
  bearGrowth: number,
  bullGrowth: number,
): ScenarioReturns {
  // Tunable positive sensitivity constant:
  // Each 1pp of growth vs. base moves the terminal multiple by ~5%.
  const k = 5;
  const MULTIPLE_FLOOR = 5.0; // Hard floor to prevent zero/negative multiples

  const computeLevel = (growth: number): ScenarioReturnItem => {
    let terminalMultiple =
      baseTerminalMultiple * (1 + k * (growth - baseGrowth));

    // Clamp to [floor, entryMultiple] (terminal can never exceed the entry multiple for an overvalued name — all three scenarios contract from the entry multiple).
    // Use Math.max(entryMultiple, baseTerminalMultiple) just in case it's undervalued.
    const maxMultiple = Math.max(entryMultiple, baseTerminalMultiple);
    terminalMultiple = Math.max(
      MULTIPLE_FLOOR,
      Math.min(terminalMultiple, maxMultiple),
    );

    const multipleChangeContribution =
      Math.pow(terminalMultiple / entryMultiple, 1 / horizonYears) - 1;
    const impliedIrr = fcfYield + growth + multipleChangeContribution;

    return {
      growth,
      terminalMultiple,
      fcfYieldContribution: fcfYield,
      growthContribution: growth,
      multipleChangeContribution,
      impliedIrr,
    };
  };

  const bear = computeLevel(bearGrowth);
  const base = computeLevel(baseGrowth);
  const bull = computeLevel(bullGrowth);

  // Hard monotonicity guard
  if (
    !(bear.impliedIrr < base.impliedIrr && base.impliedIrr < bull.impliedIrr)
  ) {
    throw new Error(
      `Scenario IRR non-monotonicity detected!\n` +
        `Fixed entry price requires IRR strictly increasing in growth.\n` +
        `Bear (g=${(bear.growth * 100).toFixed(1)}%, mult=${bear.terminalMultiple.toFixed(1)}): IRR=${(bear.impliedIrr * 100).toFixed(1)}% [FCF=${(fcfYield * 100).toFixed(1)}%, multDelta=${(bear.multipleChangeContribution * 100).toFixed(1)}%]\n` +
        `Base (g=${(base.growth * 100).toFixed(1)}%, mult=${base.terminalMultiple.toFixed(1)}): IRR=${(base.impliedIrr * 100).toFixed(1)}% [FCF=${(fcfYield * 100).toFixed(1)}%, multDelta=${(base.multipleChangeContribution * 100).toFixed(1)}%]\n` +
        `Bull (g=${(bull.growth * 100).toFixed(1)}%, mult=${bull.terminalMultiple.toFixed(1)}): IRR=${(bull.impliedIrr * 100).toFixed(1)}% [FCF=${(fcfYield * 100).toFixed(1)}%, multDelta=${(bull.multipleChangeContribution * 100).toFixed(1)}%]`,
    );
  }

  return { bear, base, bull };
}

export function buildIrrBridge(
  fcfYieldPct: number,
  growthPct: number,
  multipleDelta: number,
): string {
  const irrTotal = fcfYieldPct + growthPct + multipleDelta;
  const deltaLabel = multipleDelta >= 0 ? "expansion" : "contraction";
  const deltaStr =
    multipleDelta >= 0
      ? `+${multipleDelta.toFixed(1)}%`
      : `${multipleDelta.toFixed(1)}%`;
  return `FCF yield ${fcfYieldPct.toFixed(1)}% + growth ${growthPct.toFixed(1)}% + multiple ${deltaLabel} ${deltaStr} = IRR ${irrTotal.toFixed(1)}%`;
}
